import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle,
  Bell,
  BellRing,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  Globe,
  Image as ImageIcon,
  LogOut,
  Phone,
  Repeat,
  Star,
  Tag,
} from 'lucide-react';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import NotificationBellButton from '../../components/NotificationBellButton';
import ThemeToggle from '../../components/ThemeToggle';
import { VERSION_LABEL, BUILD_CHANNEL } from '../../version';
import { DeleteVenueSection } from '../../components/DeleteVenueSection';
import { useVenue } from '../../contexts/VenueContext';
import { clearVenueSetupReturnPath } from '../../utils/venueSetupBackNav';
import { fetchSelectedVenueApplication, getAuthHeaders, getVenueParams } from '../../utils/venueAppApi';
import { useBackTarget } from '../../hooks/useBackTarget';
import { useVenueRequiredSteps } from '../../hooks/useVenueRequiredSteps';

const DANGER = '#E53935';
const DANGER_SURFACE = 'rgba(229, 57, 53, 0.08)';
const DANGER_BORDER = 'rgba(229, 57, 53, 0.28)';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section keys an admin-tier staff member is allowed to access. Anything not
// in this set (Contact Details, Notifications & History, Payouts) is hidden
// for them. Venue owners always see the full list.
const ADMIN_ALLOWED_KEYS = new Set(['calendar', 'reviews', 'hours', 'photos', 'social']);

const ROW_BG = 'var(--accent-surface-soft)';
const DESTRUCTIVE_ROW_BG = 'rgba(229, 57, 53, 0.06)';

const SettingsRow = ({
  icon: Icon,
  label,
  summary,
  onClick,
  iconColor,
  testId,
  isLast,
  destructive = false,
  missing = false,
}) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      width: '100%',
      padding: '14px 16px',
      background: 'none',
      border: 'none',
      borderBottom: isLast ? 'none' : '1px solid var(--hairline)',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background 160ms ease',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = destructive ? DESTRUCTIVE_ROW_BG : ROW_BG; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
  >
    <span
      aria-hidden="true"
      style={{
        width: 40,
        height: 40,
        borderRadius: 14,
        background: `${iconColor}14`,
        color: iconColor,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={20} strokeWidth={1.8} />
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p
        style={{
          margin: 0,
          fontFamily: "'Inria Serif', serif",
          fontSize: 17,
          fontWeight: 700,
          color: destructive ? 'var(--status-danger-text)' : 'var(--text)',
          lineHeight: 1.2,
        }}
      >
        {label}
      </p>
      {summary ? (
        <p
          data-testid={testId ? `${testId}-summary` : undefined}
          style={{
            margin: '3px 0 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 13,
            color: 'var(--text-2)',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {summary}
        </p>
      ) : null}
    </div>
    {missing ? (
      // Right-side red dot — sits in the white space before the chevron
      // where it's easiest to spot while scrolling the list.
      <span
        data-testid={testId ? `${testId}-incomplete-badge` : undefined}
        aria-label="Step not completed"
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: DANGER,
          boxShadow: '0 0 0 3px rgba(229, 57, 53, 0.18)',
          marginRight: 6,
          flexShrink: 0,
        }}
      />
    ) : null}
    <ChevronRight size={20} color={destructive ? 'var(--status-danger-text)' : 'var(--text-2)'} strokeWidth={1.8} />
  </button>
);

const SettingsGroup = ({ title, items, testIdPrefix = 'venue-settings' }) => (
  <div style={{ width: '100%', marginBottom: 18 }}>
    {title ? (
      <p
        style={{
          margin: '0 0 8px 18px',
          fontFamily: "'Inria Serif', serif",
          fontSize: 12,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: 'var(--text-3)',
          fontWeight: 700,
        }}
      >
        {title}
      </p>
    ) : null}
    <div
      className="premium-card"
      style={{
        borderRadius: 20,
        padding: 0,
        overflow: 'hidden',
        background: 'var(--surface)',
      }}
    >
      {items.map((item, idx) => (
        <SettingsRow
          key={item.key}
          icon={item.icon}
          label={item.label}
          summary={item.summary}
          onClick={item.action}
          iconColor={item.color}
          destructive={item.destructive}
          missing={item.missing}
          testId={item.testId || `${testIdPrefix}-${item.label.toLowerCase().replace(/[\s']+/g, '-')}`}
          isLast={idx === items.length - 1}
        />
      ))}
    </div>
  </div>
);

// Build a friendly one-line summary of the venue's weekly hours.
// Examples: "Open Mon – Fri  ·  18:00 – 02:00", "Closed all week",
// "Custom · 5 days open".
function buildHoursSummary(operatingHours) {
  if (!operatingHours || typeof operatingHours !== 'object') return null;
  const DAY_KEYS = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];
  const WEEKDAY = ['mo', 'tu', 'we', 'th', 'fr'];
  const WEEKEND = ['sa', 'su'];
  const openDays = DAY_KEYS.filter((k) => {
    const v = (operatingHours[k] || '').toString().toLowerCase();
    return v && v !== 'closed' && v.includes('-');
  });
  if (openDays.length === 0) return 'Closed all week';

  // Detect uniform range across open days.
  const firstRange = operatingHours[openDays[0]];
  const allSame = openDays.every((k) => operatingHours[k] === firstRange);

  // Pretty range "18:00 – 02:00"
  const pretty = firstRange?.replace('-', ' – ');

  const sameAsWeekdays = openDays.length === WEEKDAY.length && WEEKDAY.every((k) => openDays.includes(k));
  const sameAsWeekends = openDays.length === WEEKEND.length && WEEKEND.every((k) => openDays.includes(k));
  const everyday = openDays.length === DAY_KEYS.length;

  if (allSame && everyday) return `Every day  ·  ${pretty}`;
  if (allSame && sameAsWeekdays) return `Mon – Fri  ·  ${pretty}`;
  if (allSame && sameAsWeekends) return `Sat – Sun  ·  ${pretty}`;
  if (allSame) return `${openDays.length} days  ·  ${pretty}`;
  return `Custom hours  ·  ${openDays.length} days open`;
}

export default function VenueSettingsPage({ onLogout } = {}) {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const location = useLocation();
  const { activeVenueAppId } = useVenue();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // /staff-admin/settings reuses this page — gate the section list there.
  const isAdminStaffView = location.pathname.startsWith('/staff-admin');
  // Shared "required steps" signal — paints the reminder card below the
  // page title and the red badge on each incomplete settings row.
  // Staff-admin view hides it (owner-only concern). The hook keeps
  // `steps` empty until the application fetch resolves, so no red dot
  // ever flashes on a fully-set venue on first paint.
  const { steps: requiredSteps, missingKeys, hasMissing } = useVenueRequiredSteps();
  const showActionReminder = !isAdminStaffView && hasMissing;

  const [summaries, setSummaries] = useState({});

  // Clear any leftover setup-mode return stash the moment the owner
  // opens the settings hub. Without this a stale value from an earlier
  // /venue/setup-mode session leaks and the shared sub-page back button
  // sends the owner to the setup home instead of back to /venue/settings.
  useEffect(() => {
    clearVenueSetupReturnPath();
  }, []);

  // Load lightweight summary data so each Settings row can show a one-line
  // hint of its current state.
  useEffect(() => {
    let cancelled = false;
    const loadSummaries = async () => {
      const next = {};
      try {
        const app = await fetchSelectedVenueApplication(activeVenueAppId);
        if (cancelled) return;
        next.hours = buildHoursSummary(app?.operating_hours);
        const photos = app?.photos || app?.venue_photos || [];
        if (Array.isArray(photos) && photos.length > 0) {
          next.photos = `${photos.length} photo${photos.length === 1 ? '' : 's'} uploaded`;
        } else {
          next.photos = 'Add your first photo';
        }
        const email = app?.contact?.email || app?.email;
        if (email) next.contact = email;
        const social = app?.social_links || {};
        const socialCount = Object.values(social).filter(Boolean).length;
        if (socialCount > 0) next.social = `${socialCount} link${socialCount === 1 ? '' : 's'} connected`;
        const categoryCount = Array.isArray(app?.venue_categories) ? app.venue_categories.length : 0;
        next.card = categoryCount > 0 ? `${categoryCount} categor${categoryCount === 1 ? 'y' : 'ies'} set` : 'Set your categories';
      } catch {
        // non-blocking — summaries are decorative
      }
      try {
        const res = await axios.get(`${API}/venue/blocked-dates`, {
          headers: getAuthHeaders(),
          params: getVenueParams(activeVenueAppId),
        });
        if (cancelled) return;
        const todayStr = new Date().toISOString().slice(0, 10);
        const upcoming = (res.data || []).reduce((acc, b) => {
          const start = b.start_date > todayStr ? b.start_date : todayStr;
          if (start > b.end_date) return acc;
          const days = Math.round((new Date(b.end_date) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1;
          return acc + days;
        }, 0);
        next.calendar = upcoming > 0
          ? `${upcoming} upcoming blocked day${upcoming === 1 ? '' : 's'}`
          : 'No upcoming closures';
      } catch {
        // ignore
      }
      if (!cancelled) setSummaries(next);
    };
    if (activeVenueAppId) loadSummaries();
    return () => { cancelled = true; };
  }, [activeVenueAppId]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onLogout) onLogout();
    window.location.href = '/login';
  };

  const handleSwitchRole = () => {
    navigate('/switch-role', { state: { fromPath: location.pathname } });
  };

  // Admin staff hits this page via `/staff-admin/settings`. Their allowed
  // sub-pages must be served under `/staff-admin/settings/...` because the
  // `/venue/...` tree is guarded by `requiredRole="venue_owner"` (clicking a
  // /venue/settings/* path would bounce them back to /staff-admin/people).
  const settingsBase = isAdminStaffView ? '/staff-admin/settings' : '/venue/settings';

  const allItems = {
    calendar: {
      key: 'calendar', label: 'Calendar', icon: Calendar, color: 'var(--accent-purple)',
      path: `${settingsBase}/calendar`, summary: summaries.calendar,
    },
    reviews: {
      key: 'reviews', label: 'Ratings & Reviews', icon: Star, color: '#F5A623',
      path: `${settingsBase}/reviews`, summary: summaries.reviews,
    },
    contact: {
      key: 'contact', label: 'Contact Details', icon: Phone, color: '#2D7BF0',
      path: `${settingsBase}/contact`, summary: summaries.contact || 'Set up email',
    },
    hours: {
      key: 'hours', label: 'Operating Hours', icon: Clock, color: '#00B341',
      path: `${settingsBase}/hours`, summary: summaries.hours || 'Set your weekly schedule',
    },
    notifications: {
      key: 'notifications', label: 'Notifications & History', icon: BellRing, color: '#E53935',
      path: `${settingsBase}/notifications`, summary: summaries.notifications,
    },
    payouts: {
      key: 'payouts', label: 'Payouts', icon: CreditCard, color: '#00B341',
      path: `${settingsBase}/payouts`, summary: summaries.payouts,
    },
    photos: {
      key: 'photos', label: "Venues' Photos", icon: ImageIcon, color: '#D946EF',
      path: `${settingsBase}/photos`, summary: summaries.photos,
    },
    social: {
      key: 'social', label: 'Social Media', icon: Globe, color: '#0EA5E9',
      path: `${settingsBase}/social`, summary: summaries.social || 'Link Instagram, TikTok, web',
    },
    card: {
      key: 'card', label: 'Venue Card', icon: Tag, color: '#8B5CF6',
      path: `${settingsBase}/card`, summary: summaries.card,
    },
  };

  // Group sections for a premium two-card layout. The grouping below mirrors
  // the natural mental model: Operations (calendar/hours/photos/social/reviews)
  // sits at the top, then Account-level (contact/notifications/payouts).
  const operationsKeys = ['calendar', 'reviews', 'hours', 'photos', 'social', 'card'];
  const accountKeys = ['contact', 'notifications', 'payouts'];

  const buildItems = (keys) =>
    keys
      .filter((k) => (isAdminStaffView ? ADMIN_ALLOWED_KEYS.has(k) : true))
      .map((k) => ({
        ...allItems[k],
        action: () => navigate(allItems[k].path),
        // Only owner-facing rows ever paint the red badge — staff-admin
        // view skips it so admins aren't nagged about owner setup. Hook
        // guards against painting the badge until the fetch resolves,
        // so a fully-set venue never flashes red on first paint.
        missing: !isAdminStaffView
          && Object.prototype.hasOwnProperty.call(requiredSteps, k)
          && requiredSteps[k] === false,
      }));

  const operationsItems = buildItems(operationsKeys);
  const accountItems = buildItems(accountKeys);

  // Session group surfaces Switch Role + Log Out right inside Settings so the
  // owner doesn't have to bounce through the Profile page to log out.
  const sessionItems = [
    {
      key: 'switch',
      label: 'Switch role',
      icon: Repeat,
      color: 'var(--accent-purple)',
      action: handleSwitchRole,
      testId: 'venue-settings-switch-role',
    },
    {
      key: 'logout',
      label: 'Log out',
      icon: LogOut,
      color: '#E53935',
      destructive: true,
      action: handleLogout,
      testId: 'venue-settings-logout',
    },
  ];

  return (
    <div
      className="app-shell"
      data-testid="venue-settings-page"
      style={{
        minHeight: '100vh',
        background: 'var(--app-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0' }}>
        <button
          onClick={goBack}
          data-testid="venue-settings-back"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <ChevronLeft size={30} color="var(--text)" strokeWidth={2} />
        </button>
        {NotificationBellButton ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ThemeToggle />
            <NotificationBellButton />
          </div>
        ) : (
          <Bell size={26} color="var(--text)" strokeWidth={1.3} />
        )}
      </div>

      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-settings-shared-logo" />
      </div>

      <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 8, marginBottom: 24 }}>
        Venue Settings
      </h2>

      {showActionReminder ? (
        <div
          data-testid="venue-settings-action-reminder"
          data-missing-count={missingKeys.length}
          role="status"
          style={{
            width: 'calc(100% - 32px)',
            maxWidth: 360,
            marginBottom: 18,
            padding: '14px 16px',
            borderRadius: 18,
            background: DANGER_SURFACE,
            border: `1px solid ${DANGER_BORDER}`,
            boxShadow: '0 8px 18px rgba(229, 57, 53, 0.10)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            textAlign: 'left',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: 'rgba(229, 57, 53, 0.14)',
              color: DANGER,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={18} strokeWidth={2.2} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              data-testid="venue-settings-action-reminder-title"
              style={{
                margin: 0,
                fontFamily: "'Inria Serif', serif",
                fontSize: 15,
                fontWeight: 700,
                color: DANGER,
                lineHeight: 1.25,
              }}
            >
              Action needed
            </p>
            <p
              data-testid="venue-settings-action-reminder-subtitle"
              style={{
                margin: '4px 0 0',
                fontFamily: "'Inria Serif', serif",
                fontSize: 13,
                color: 'var(--text-2)',
                lineHeight: 1.4,
                fontWeight: 500,
              }}
            >
              {missingKeys.length === 1
                ? 'One required step below is missing — your venue stays off the guest map until it\u2019s complete.'
                : `${missingKeys.length} required steps below are missing — your venue stays off the guest map until they\u2019re complete.`}
            </p>
          </div>
        </div>
      ) : null}

      <div style={{ width: 'calc(100% - 32px)', maxWidth: 360 }}>
        {operationsItems.length ? <SettingsGroup title="Operations" items={operationsItems} /> : null}
        {accountItems.length ? <SettingsGroup title="Account" items={accountItems} /> : null}
        <SettingsGroup title="Session" items={sessionItems} />
      </div>

      {!isAdminStaffView && (
        <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 4 }}>
          <DeleteVenueSection dataTestIdPrefix="venue-settings" />
        </div>
      )}

      <div style={{ flex: 1 }} />
      <p
        data-testid="venue-settings-account-id"
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 13,
          color: 'var(--text-3)',
          marginBottom: 6,
          marginTop: 24,
          letterSpacing: 0.2,
        }}
      >
        Account ID · {user.customer_id || '—'}
      </p>
      <p
        data-testid="venue-settings-app-version"
        style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', marginBottom: 30, letterSpacing: 0.3 }}
      >
        Allure {VERSION_LABEL}{BUILD_CHANNEL !== 'production' ? ` · ${BUILD_CHANNEL}` : ''}
      </p>
    </div>
  );
}
